import { T } from '../../copy/text'
import { planCardState, type PlanCardInput } from './plan'

/**
 * 「开始做」那张卡（设计稿 goal）。
 *
 * # 它在输入框上方，不是 Modal
 *
 * 与绑定卡同一个位置、同一个理由：设计稿里它是**消息流里的一张卡**，
 * 不盖住输入框。用户随时可以不理它、接着说话。
 *
 * # 卡上只有两行，而设计稿画了五行
 *
 * 「影响范围」与「能不能撤」没有数据来源（能撤的是那条任务，
 * 不是这份计划说的那次改动）。画出来再填一句话，那句话是编的——
 * 而这张卡说的是「Lulu 打算做什么」，编出来的内容会被用户当成承诺。
 * 理由与 `toRedDecision`「桥不出来的就说没有」同一条。
 */
export function PlanCard({
  plan,
  projectId,
  archived,
  justScheduledTaskId,
  onStart,
  onRevise,
  starting,
  failed,
}: PlanCardInput & {
  /** 真发 `POST /conversations/{id}/schedule`。不给就不画卡。 */
  readonly onStart?: () => void
  /** 「我改一下目标」：把光标放回输入框。**不发任何请求。** */
  readonly onRevise?: () => void
  readonly starting?: boolean
  /** 上一次没安排成。**留在原地说清楚，不跳转。** */
  readonly failed?: boolean
}) {
  const state = planCardState({ plan, projectId, archived, justScheduledTaskId })
  if (state.kind === 'hidden') return null
  // 没有 onStart 就等于这颗按钮点不动 —— 那是错的形状，整块不画。
  // （台账记过三次「界面有控件、真实动作没接」，这里不再来第四次。）
  if (onStart === undefined) return null
  const scheduled = state.kind === 'scheduled'
  return (
    <div
      data-testid="lulu-plan-card"
      data-state={state.kind}
      data-task-id={scheduled ? state.taskId : undefined}
      className="mb-2 rounded-npc-card border border-npc-lulu-line-2 bg-npc-lulu-bg-2 p-2.5"
    >
      <p className="text-npc-caption text-npc-lulu-t2">
        <T id="lulu_workbench.plan.headline" />
      </p>
      <dl className="mt-1.5 grid gap-1">
        <div className="flex gap-2">
          <dt className="shrink-0 text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.plan.objective_label" />
          </dt>
          {/* min-w-0 不能省：长目标会把这一行撑破（验收 #43 那个坑）。 */}
          <dd data-testid="lulu-plan-objective" className="min-w-0 text-npc-caption text-npc-lulu-t1">
            {plan?.objective}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0 text-npc-meta text-npc-lulu-t3">
            <T id="lulu_workbench.plan.acceptance_label" />
          </dt>
          <dd className="min-w-0">
            <ul className="grid gap-0.5">
              {(plan?.acceptance ?? []).map((line, i) => (
                <li
                  key={`${i}-${line}`}
                  data-testid="lulu-plan-acceptance"
                  className="text-npc-caption text-npc-lulu-t1"
                >
                  {line}
                </li>
              ))}
            </ul>
          </dd>
        </div>
      </dl>

      {scheduled ? (
        <p data-testid="lulu-plan-already" className="mt-2 text-npc-meta text-npc-lulu-t3">
          <T id="lulu_workbench.plan.already" />
        </p>
      ) : null}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          data-testid="lulu-plan-start"
          // 已经安排过就按不动。让它能点的话，用户会去撞服务端那句
          // ErrAlreadyScheduled ——而那堵墙我们早就知道。
          disabled={scheduled || Boolean(starting)}
          onClick={() => {
            if (scheduled || starting) return
            onStart()
          }}
          className="rounded-npc-button bg-npc-lulu-ok px-3 py-1 text-npc-caption text-npc-lulu-bg-0 disabled:opacity-40"
        >
          <T id="lulu_workbench.plan.start" />
        </button>
        {onRevise === undefined ? null : (
          <button
            type="button"
            data-testid="lulu-plan-revise"
            onClick={onRevise}
            className="rounded-npc-chip border border-npc-lulu-line-2 px-2 py-0.5 text-npc-meta text-npc-lulu-t2"
          >
            <T id="lulu_workbench.plan.revise" />
          </button>
        )}
      </div>

      {failed ? (
        <p data-testid="lulu-plan-failed" className="mt-1.5 text-npc-meta text-npc-lulu-warn">
          <T id="lulu_workbench.plan.failed" />
        </p>
      ) : null}
    </div>
  )
}
